import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageToggle } from '@/components/ui/LanguageSelector';

// Sanitize the `from` route received from <ProtectedRoute state={{from:location}}>
// before redirecting after login. We MUST only accept internal paths so an
// open-redirect cannot be crafted by sending a victim to /login with a
// poisoned `state.from`. Returns null when the value is missing or unsafe,
// in which case the caller falls back to its default destination.
function _safeReturnPath(from) {
  if (!from || typeof from !== 'object') return null;
  const pathname = typeof from.pathname === 'string' ? from.pathname : '';
  // Must start with a single "/", reject "//evil.com" and protocol URLs.
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return null;
  // Don't bounce back into auth/onboarding gates — those are owned by the
  // post-login flow itself.
  const forbidden = ['/login', '/signup', '/forgot-password', '/reset-password',
    '/change-password', '/onboarding', '/billing/required'];
  if (forbidden.includes(pathname)) return null;
  const search = typeof from.search === 'string' ? from.search : '';
  const hash = typeof from.hash === 'string' ? from.hash : '';
  return pathname + search + hash;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error: authError } = useAuth();
  const { t, language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await login(loginEmail, loginPassword);
      
      // Check if user needs to change password
      if (userData.must_change_password) {
        navigate('/change-password');
        return;
      }

      // Honor the protected-route deep link captured by ProtectedRoute
      // (location.state.from). Workspace gate still wins: a user without
      // a workspace cannot deep-link past onboarding.
      const returnTo = _safeReturnPath(location.state?.from);

      // Check user role and redirect accordingly
      if (userData.role === 'admin') {
        navigate(returnTo || '/admin');
      } else if (userData.has_workspace) {
        navigate(returnTo || '/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
      {/* Language Toggle - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
            Space<span className="text-primary">LeadX</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === 'fr' 
              ? 'Plateforme de gestion de prospects pour entrepreneurs locaux'
              : 'Lead management platform for local entrepreneurs'}
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold">
              {t('auth.signIn')}
            </CardTitle>
            <CardDescription>
              {t('auth.signInDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(error || authError) && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error || authError}</AlertDescription>
              </Alert>
            )}

            {/* Email/Password Login Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    className="pl-10"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-btn"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid="login-forgot-password-link"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 font-bold"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {t('auth.signInButton')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('auth.termsNotice')}{' '}
          <Link 
            to={language === 'fr' ? '/fr/conditions-dutilisation' : '/en/terms-of-use'} 
            className="text-primary hover:underline"
          >
            {t('auth.termsLink')}
          </Link>
        </p>
        
        <p className="text-center text-sm text-muted-foreground mt-4" data-testid="login-signup-prompt">
          {language === 'fr' ? 'Pas encore de compte ?' : "Don't have an account yet?"}{' '}
          <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="login-to-signup-link">
            {language === 'fr' ? 'Créer un compte' : 'Create an account'}
          </Link>
        </p>
      </div>
    </div>
  );
}
